import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Plus, Pencil, Trash2, Package, DollarSign, AlertCircle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RentalItems = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    serial_number: "",
    description: "",
    quantity: 1,
    rental_price: 0,
    billing_period: "day",
    condition: "good",
    notes: "",
  });

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/rental-items?include_inactive=true`);
      setItems(response.data);
    } catch (error) {
      toast.error("Failed to load rental items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/rental-items/${editingItem.item_id}`, formData);
        toast.success("Rental item updated successfully");
      } else {
        await axios.post(`${API}/rental-items`, formData);
        toast.success("Rental item created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save rental item");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      serial_number: item.serial_number || "",
      description: item.description || "",
      quantity: item.quantity,
      rental_price: item.rental_price,
      billing_period: item.billing_period,
      condition: item.condition,
      notes: item.notes || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this rental item?")) return;
    try {
      await axios.delete(`${API}/rental-items/${itemId}`);
      toast.success("Rental item deleted successfully");
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete rental item");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      name: "",
      serial_number: "",
      description: "",
      quantity: 1,
      rental_price: 0,
      billing_period: "day",
      condition: "good",
      notes: "",
    });
  };

  const handleRecalculateInventory = async () => {
    setRecalculating(true);
    try {
      const response = await axios.post(`${API}/rental-items/recalculate-inventory`, {}, {
        withCredentials: true,
      });
      toast.success(response.data.message);
      if (response.data.updated_items?.length > 0) {
        console.log("Updated items:", response.data.updated_items);
      }
      fetchItems(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to recalculate inventory");
    } finally {
      setRecalculating(false);
    }
  };

  const formatPrice = (price, period) => {
    return `R ${price.toFixed(2)}/${period}`;
  };

  const getConditionBadge = (condition) => {
    const variants = {
      good: "default",
      fair: "secondary",
      poor: "outline",
      damaged: "destructive",
    };
    return <Badge variant={variants[condition] || "secondary"}>{condition}</Badge>;
  };

  const getStatusBadge = (status) => {
    const colors = {
      available: "bg-green-100 text-green-800",
      partially_rented: "bg-yellow-100 text-yellow-800",
      fully_rented: "bg-red-100 text-red-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || "bg-slate-100"}`}>
        {status.replace("_", " ")}
      </span>
    );
  };

  const activeItems = items.filter(i => i.is_active);
  const totalAvailable = activeItems.reduce((sum, i) => sum + i.available_quantity, 0);
  const totalRented = activeItems.reduce((sum, i) => sum + (i.quantity - i.available_quantity), 0);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="rental-items-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Rental Items</h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage returnable stock items available for rental
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleRecalculateInventory}
            disabled={recalculating}
            data-testid="recalculate-inventory-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
            {recalculating ? 'Recalculating...' : 'Sync Inventory'}
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-rental-item-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Rental Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                {editingItem ? "Edit Rental Item" : "Add New Rental Item"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Item Name *</Label>
                  <Input
                    id="name"
                    data-testid="rental-item-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., CO2 Cylinder"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="serial_number">Serial Number</Label>
                  <Input
                    id="serial_number"
                    data-testid="rental-item-serial"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder="e.g., CYL-001"
                  />
                </div>
                <div>
                  <Label htmlFor="quantity">Quantity *</Label>
                  <Input
                    id="quantity"
                    type="number"
                    data-testid="rental-item-quantity"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })}
                    min="1"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="rental-item-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Item description..."
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="rental_price">Rental Price (R) *</Label>
                  <Input
                    id="rental_price"
                    type="number"
                    data-testid="rental-item-price"
                    value={formData.rental_price}
                    onChange={(e) => setFormData({ ...formData, rental_price: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="billing_period">Billing Period</Label>
                  <Select
                    value={formData.billing_period}
                    onValueChange={(value) => setFormData({ ...formData, billing_period: value })}
                  >
                    <SelectTrigger data-testid="rental-item-billing-period">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Per Day</SelectItem>
                      <SelectItem value="week">Per Week</SelectItem>
                      <SelectItem value="month">Per Month</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="condition">Condition</Label>
                  <Select
                    value={formData.condition}
                    onValueChange={(value) => setFormData({ ...formData, condition: value })}
                  >
                    <SelectTrigger data-testid="rental-item-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="good">Good</SelectItem>
                      <SelectItem value="fair">Fair</SelectItem>
                      <SelectItem value="poor">Poor</SelectItem>
                      <SelectItem value="damaged">Damaged</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  data-testid="rental-item-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-rental-item-btn">
                  {editingItem ? "Update" : "Create"} Item
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total Items</CardTitle>
            <Package className="h-4 w-4 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeItems.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Available</CardTitle>
            <Package className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{totalAvailable}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Currently Rented</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{totalRented}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total Value</CardTitle>
            <DollarSign className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              R {activeItems.reduce((sum, i) => sum + (i.quantity * i.rental_price), 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>Rental Inventory</CardTitle>
          <CardDescription>
            Stock items available for rental to customers
          </CardDescription>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No rental items defined yet</p>
              <p className="text-sm">Add your first rental item to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item Name</TableHead>
                  <TableHead>Serial #</TableHead>
                  <TableHead>Qty (Avail/Total)</TableHead>
                  <TableHead>Rental Price</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow 
                    key={item.item_id} 
                    data-testid={`rental-item-row-${item.item_id}`}
                    className={`cursor-pointer hover:bg-slate-50 ${!item.is_active ? "opacity-50" : ""}`}
                    onClick={() => handleEdit(item)}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.name}</div>
                        {item.description && (
                          <div className="text-xs text-slate-500 truncate max-w-xs">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {item.serial_number || "-"}
                    </TableCell>
                    <TableCell>
                      <span className={item.available_quantity === 0 ? "text-red-600 font-medium" : ""}>
                        {item.available_quantity}
                      </span>
                      <span className="text-slate-400"> / {item.quantity}</span>
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatPrice(item.rental_price, item.billing_period)}
                    </TableCell>
                    <TableCell>{getConditionBadge(item.condition)}</TableCell>
                    <TableCell>{getStatusBadge(item.status)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-rental-item-${item.item_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(item.item_id)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-rental-item-${item.item_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RentalItems;
